import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { dispatch, useSelector } from '../../../redux/store';
import {
  cancelPosDrawerTransfer,
  createPosDrawerTransfer,
  getPosDrawerSessionContext,
  getPosDrawerHandoverSummary,
  getPosDrawerTransferRecipients,
  reassignPosDrawerTransfer,
  setTransferDialogOpen,
} from '../../../redux/slices/posRedux/posRedux';
import POSHandoverSummary from './POSHandoverSummary';

const formatRequestedAt = (value) => {
  const requestedAt = moment(value);
  return requestedAt.isValid() ? requestedAt.format('YYYY-MM-DD HH:mm') : 'Unavailable';
};

export default function POSTransferDialog() {
  const { transferDialogOpen, sessionContext, eligibleTransferRecipients, handoverSummary, requests } = useSelector((state) => state?.pos);
  const [selectedRecipientId, setSelectedRecipientId] = useState('');
  const [isChangingRecipient, setIsChangingRecipient] = useState(false);
  const [lastAction, setLastAction] = useState(null);
  const [valuesConfirmed, setValuesConfirmed] = useState(false);
  const [summaryScope, setSummaryScope] = useState('drawer');

  const outgoingTransfer = sessionContext?.outgoingPendingTransfer;
  const activeSession = sessionContext?.activeSession;
  const drawer = sessionContext?.drawer;
  const mutationInProgress = requests.createTransfer.loading || requests.reassignTransfer.loading || requests.cancelTransfer.loading;

  const refreshSummary = useCallback(async () => {
    // Any refreshed financial values must be reviewed again before custody is offered.
    setValuesConfirmed(false);
    setSummaryScope('drawer');
    try {
      return await dispatch(getPosDrawerHandoverSummary(outgoingTransfer?.id));
    } catch (_error) {
      return null;
    }
  }, [outgoingTransfer?.id]);

  const fetchRecipients = useCallback(async () => {
    setLastAction('eligibleTransferRecipients');
    try {
      const response = await dispatch(getPosDrawerTransferRecipients());
      setLastAction(null);
      return response?.data || [];
    } catch (_error) {
      return null;
    }
  }, []);

  const refreshContext = useCallback(async () => {
    setLastAction('sessionContext');
    try {
      const response = await dispatch(getPosDrawerSessionContext());
      setLastAction(null);
      if (!response?.data?.outgoingPendingTransfer) await fetchRecipients();
      return response?.data;
    } catch (_error) {
      return null;
    }
  }, [fetchRecipients]);

  useEffect(() => {
    if (!transferDialogOpen) return;

    setSelectedRecipientId('');
    setIsChangingRecipient(false);
    setLastAction(null);
    setValuesConfirmed(false);
    setSummaryScope('drawer');
    refreshContext();
    refreshSummary();
  }, [transferDialogOpen, refreshContext, refreshSummary]);

  const handleClose = () => {
    if (mutationInProgress) return;
    setSelectedRecipientId('');
    setIsChangingRecipient(false);
    setLastAction(null);
    setValuesConfirmed(false);
    dispatch(setTransferDialogOpen(false));
  };

  const getVerifiedRecipient = async () => {
    // The list is advisory; refresh it immediately before mutation while the backend remains authoritative.
    const latestRecipients = await fetchRecipients();
    if (!latestRecipients) return null;

    const recipient = latestRecipients.find((value) => Number(value.id) === Number(selectedRecipientId));
    if (!recipient) setLastAction('recipientUnavailable');
    return recipient || null;
  };

  const handleCreate = async () => {
    const recipient = await getVerifiedRecipient();
    if (!recipient) return;
    if (!window.confirm(`Transfer this register to ${recipient.name}? You remain responsible until they accept.`)) return;

    setLastAction('createTransfer');
    try {
      await dispatch(createPosDrawerTransfer(recipient.id));
      setSelectedRecipientId('');
      setLastAction(null);
      setValuesConfirmed(false);
      await dispatch(getPosDrawerHandoverSummary());
    } catch (_error) {
      // The request error remains in Redux so the dialog can explain the recoverable failure.
    }
  };

  const handleReassign = async () => {
    const recipient = await getVerifiedRecipient();
    if (!recipient) return;
    if (!window.confirm(`Change the pending register transfer to ${recipient.name}?`)) return;

    setLastAction('reassignTransfer');
    try {
      await dispatch(reassignPosDrawerTransfer(outgoingTransfer.id, recipient.id));
      setSelectedRecipientId('');
      setIsChangingRecipient(false);
      setLastAction(null);
      setValuesConfirmed(false);
      await dispatch(getPosDrawerHandoverSummary(outgoingTransfer.id));
    } catch (_error) {
      // Keep change-recipient mode open so the cashier can correct or retry the request.
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this pending register transfer?')) return;

    setLastAction('cancelTransfer');
    try {
      await dispatch(cancelPosDrawerTransfer(outgoingTransfer.id));
      setSelectedRecipientId('');
      setIsChangingRecipient(false);
      setLastAction(null);
      await fetchRecipients();
    } catch (_error) {
      // Preserve the pending view and expose the backend message below.
    }
  };

  const handleChangeRecipient = async () => {
    setSelectedRecipientId('');
    setIsChangingRecipient(true);
    setValuesConfirmed(false);
    await fetchRecipients();
  };

  const requestError = lastAction && requests[lastAction]?.error?.message;
  const errorMessage = lastAction === 'recipientUnavailable' ? 'The selected cashier is no longer eligible. Please choose another cashier.' : requestError;
  const recipientsLoading = requests.eligibleTransferRecipients.loading;
  const contextLoading = requests.sessionContext.loading;

  const renderSessionDetails = () => (
    <Paper variant='outlined' sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} divider={<Divider orientation='vertical' flexItem />}>
        <Typography variant='body2'>Drawer ID: {drawer?.id || 'Unavailable'}</Typography>
        <Typography variant='body2'>Session ID: {activeSession?.id || 'Unavailable'}</Typography>
        <Typography variant='body2'>Sequence: {activeSession?.sequenceNo || 'Unavailable'}</Typography>
      </Stack>
    </Paper>
  );

  const renderRecipientSelection = () => (
    <Box>
      <Typography variant='subtitle1' sx={{ mb: 1 }}>
        Select the next cashier
      </Typography>
      {recipientsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={30} />
        </Box>
      ) : eligibleTransferRecipients.length === 0 ? (
        <Alert severity='info'>No eligible cashier is currently available at this location. Refresh later or close this dialog.</Alert>
      ) : (
        <RadioGroup value={selectedRecipientId} onChange={(event) => { setSelectedRecipientId(event.target.value); setValuesConfirmed(false); }}>
          <Stack spacing={1}>
            {eligibleTransferRecipients.map((recipient) => (
              <Paper key={recipient.id} variant='outlined' sx={{ px: 1.5 }}>
                <FormControlLabel
                  value={String(recipient.id)}
                  control={<Radio />}
                  sx={{ width: '100%', m: 0, py: 1 }}
                  label={
                    <Stack direction='row' spacing={1.5} alignItems='center'>
                      <Avatar src={recipient.userImage || undefined} alt={recipient.name} />
                      <Typography variant='body1'>{recipient.name}</Typography>
                    </Stack>
                  }
                />
              </Paper>
            ))}
          </Stack>
        </RadioGroup>
      )}
    </Box>
  );

  return (
    <Dialog
      open={transferDialogOpen}
      onClose={handleClose}
      maxWidth={false}
      scroll='paper'
      PaperProps={{ sx: { width: '98vw', height: '98vh', maxWidth: '98vw', m: 1 } }}
    >
      <DialogTitle>{outgoingTransfer ? 'Pending Register Transfer' : 'Transfer Register'}</DialogTitle>
      <DialogContent dividers>
        {contextLoading && !sessionContext ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={36} /></Box> : !activeSession || drawer?.status !== 'open' ? <Alert severity='warning'>An active POS register session is not available. Refresh your session or close this dialog.</Alert> : (
          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={9}>
              <POSHandoverSummary summary={handoverSummary} loading={requests.handoverSummary.loading} error={requests.handoverSummary.error?.message} scope={summaryScope} onScopeChange={setSummaryScope} onRefresh={refreshSummary} />
            </Grid>
            <Grid item xs={12} lg={3}>
              <Paper variant='outlined' sx={{ p: 2, borderRadius: 3, position: { lg: 'sticky' }, top: 0 }}>
                <Stack spacing={2}>
                  <Typography variant='h6'>Transfer Details</Typography>
                  {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
                  {renderSessionDetails()}
                  <Alert severity='info'>You remain responsible for this register and may continue using POS until the selected cashier accepts.</Alert>
                  {outgoingTransfer && !isChangingRecipient ? (
                    <Stack direction='row' spacing={1.5} alignItems='center'><Avatar src={outgoingTransfer?.recipient?.userImage || undefined} alt={outgoingTransfer?.recipient?.name} /><Box><Typography variant='subtitle1'>{outgoingTransfer?.recipient?.name || 'Unknown cashier'}</Typography><Typography variant='body2' color='text.secondary'>Requested {formatRequestedAt(outgoingTransfer?.createdAt)}</Typography></Box></Stack>
                  ) : (
                    <>{renderRecipientSelection()}<FormControlLabel control={<Checkbox checked={valuesConfirmed} disabled={!handoverSummary || requests.handoverSummary.loading} onChange={(event) => setValuesConfirmed(event.target.checked)} />} label='I have reviewed these values and confirm they are correct to the best of my knowledge.' /></>
                  )}
                  {outgoingTransfer && !isChangingRecipient ? (
                    <><Button variant='outlined' onClick={handleChangeRecipient} disabled={mutationInProgress || contextLoading}>Change Recipient</Button><Button color='error' variant='outlined' onClick={handleCancel} disabled={mutationInProgress || contextLoading}>Cancel Transfer</Button><Button onClick={refreshContext} disabled={mutationInProgress || contextLoading}>Refresh Status</Button></>
                  ) : (
                    <>{outgoingTransfer && <Button onClick={() => { setIsChangingRecipient(false); setValuesConfirmed(false); }} disabled={mutationInProgress}>Back</Button>}<LoadingButton variant='contained' loading={isChangingRecipient ? requests.reassignTransfer.loading : requests.createTransfer.loading} disabled={!selectedRecipientId || !valuesConfirmed || !handoverSummary || mutationInProgress || recipientsLoading || requests.handoverSummary.loading} onClick={isChangingRecipient ? handleReassign : handleCreate}>{isChangingRecipient ? 'Confirm Change' : 'Transfer Register'}</LoadingButton></>
                  )}
                  <Button color='inherit' onClick={handleClose} disabled={mutationInProgress}>Close</Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
}
